import { lazy, ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '../Contexts/AuthContext'

import Login from '../Components/Layers/Login'
import ProtectedRoute from './ProtectedRoute'

const Main = lazy(() => import('../Views/Main'))
const MessagesPage = lazy(() => import('../Components/Mobile/MessagesPage'))
const Cart = lazy(() => import('../Components/Layers/map'))
const Model3dViewer = lazy(() => import('../Views/Model3dViewer'))
const MainDashboard = lazy(() => import('../Views/MainDashboard'))
const SettingsLayout = lazy(() => import('../Views/SettingsLayout'))
const AddUser = lazy(() => import('../Components/Dashboard/AddUser'))
const UserList = lazy(() => import('../Components/Dashboard/UserList'))
const Dashboard = lazy(() => import('../Components/Dashboard/Dashboard'))
const AddResort = lazy(() => import('../Components/Dashboard/AddResort'))
const ListResorts = lazy(() => import('../Components/Dashboard/ListResorts'))
const ChartDashboard = lazy(() => import('../Components/Charts/chartDashboard'))
const LineChart = lazy(() => import('../Components/Charts/LineChart'))
const BarChart = lazy(() => import('../Components/Charts/BarChart'))
const ChioneTab = lazy(() => import('../Components/Charts/chionetab'))
const PieChart = lazy(() => import('../Components/Charts/pieChart'))
const TemperatureChart = lazy(() => import('../Components/Charts/Temperature'))
const EpNeige = lazy(() => import('../Components/Charts/MesureEpNeige'))
const ChangerMdp = lazy(
  () => import('../Components/utils/components/ChangerMdp'),
)
const NotFound = lazy(() => import('../Components/utils/components/404'))
const Unauthorized = lazy(
  () => import('../Components/utils/components/Unauthorized'),
)
const DashboardDamage = lazy(
  () => import('../Components/Elda-Damage/Dashboard'),
)
const NewPlan = lazy(
  () => import('../Components/Elda-Damage/PlanManagement/NewPlan/NewPlan'),
)
const CurrentPlan = lazy(
  () => import('../Components/Elda-Damage/CurrentPlan/CurrentPlan'),
)
const MachineList = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/GroomingMachines/MachineList'
    ),
)
const MachineEntry = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/GroomingMachines/MachineEntry'
    ),
)
const DriversList = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/DriversManagement/DriversList'
    ),
)
const DriverEntry = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/DriversManagement/DriverEntry'
    ),
)
const AddLocations = lazy(
  () => import('../Components/Dashboard/Locations/AddLocationsPage'),
)
const ConnexionHistory = lazy(
  () => import('../Components/Dashboard/Connexion_history'),
)
const UploadGpkg = lazy(() => import('../Components/Dashboard/UploadGpkg'))
const SnowCannonManagement = lazy(
  () => import('../Components/Dashboard/SnowCannonManagement'),
)
const CustomLocationsAdmin = lazy(
  () =>
    import('../Components/Elda-Damage/SpecialTask/pages/CustomLocationsAdmin'),
)
const SpecialTasksAnalysisPage = lazy(
  () =>
    import(
      '../Components/Elda-Damage/SpecialTaskAnalysis/pages/SpecialTasksAnalysisPage'
    ),
)
const GroupsDashboard = lazy(
  () => import('../Components/Dashboard/GroupsDashboard'),
)
const GroomingDataList = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/GroomingDataManager/GroomingDataList'
    ),
)
const GroomingDataForm = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/GroomingDataManager/GroomingDataForm'
    ),
)
const GroomingDataView = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/GroomingDataManager/GroomingDataView'
    ),
)
const GroomingAnalyticsPage = lazy(
  () => import('../Components/Elda-Damage/Analytics/GroomingAnalyticsLayout'),
)
const SpecialTaskWithProvider = lazy(
  () =>
    import(
      '../Components/Elda-Damage/SpecialTask/pages/SpecialTaskWithProvider'
    ),
)

const MachineView = lazy(
  () =>
    import(
      '../Components/Elda-Damage/ResourceManagement/GroomingMachines/MachineView'
    ),
)

const MarkerCategoriesAdmin = lazy(
  () => import('../Components/Layers/modals/MarkerCategoriesAdmin'),
)

const ElevationProfilePage = lazy(
  () => import('../Components/ElevationProfile/pages/ElevationProfilePage'),
)

const SlopeEditorPage = lazy(
  () => import('../Components/SlopeEditor/pages/SlopeEditorPage'),
)

const MessagePage = lazy(
  () => import('../Components/Messages/pages/MessagePage'),
)

const WhatsNewPostView = lazy(
  () => import('../Components/WhatsNew/WhatsNewPostView'),
)
const WhatsNewAdminList = lazy(
  () => import('../Components/WhatsNew/admin/WhatsNewAdminList'),
)
const WhatsNewEditor = lazy(
  () => import('../Components/WhatsNew/admin/WhatsNewEditor'),
)

export const AppRoutes = (): ReactNode => {
  const { isAuthenticated } = useAuth()

  return (
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />}
      />
      <Route path="/mdpchange" element={<ChangerMdp />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Main />
          </ProtectedRoute>
        }
      >
        <Route path="" element={<Cart />} />
      </Route>

      <Route
        path="/elevation-profile"
        element={
          <ProtectedRoute>
            <ElevationProfilePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/messages/:id"
        element={
          <ProtectedRoute>
            <MessagePage />
          </ProtectedRoute>
        }
      />

      {/* Settings Routes (Profile Management - No Admin Permission Required) */}
      <Route
        path="/settings/*"
        element={
          <ProtectedRoute>
            <SettingsLayout />
          </ProtectedRoute>
        }
      />

      {/* Messages (mobile only) */}
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Dashboard Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredPermission="admin:access">
            <MainDashboard />
          </ProtectedRoute>
        }
      >
        <Route path="" element={<Dashboard />} />
        <Route path="groups" element={<GroupsDashboard />} />
        <Route path="users" element={<UserList />} />
        <Route
          path="add-user"
          element={
            <ProtectedRoute requiredPermission="admin:manage_users">
              <AddUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="resorts"
          element={
            <ProtectedRoute requiredRole="Super Admin">
              <ListResorts />
            </ProtectedRoute>
          }
        />
        <Route
          path="add-resort"
          element={
            <ProtectedRoute requiredRole="Super Admin">
              <AddResort />
            </ProtectedRoute>
          }
        />
        <Route path="locations" element={<AddLocations />} />
        <Route path="upload" element={<UploadGpkg />} />
        <Route path="history" element={<ConnexionHistory />} />
        <Route
          path="snow-cannons"
          element={
            <ProtectedRoute requiredModule="snow_cannons">
              <SnowCannonManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="custom-locations"
          element={
            <ProtectedRoute requiredPermission="admin:manage_roles">
              <CustomLocationsAdmin />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Damage Management Routes */}
      <Route
        path="/damage"
        element={
          <ProtectedRoute requiredPermission="plans:read">
            <DashboardDamage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/new-plan"
        element={
          <ProtectedRoute requiredPermission="plans:manage">
            <NewPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/special-task"
        element={
          <ProtectedRoute requiredPermission="tasks:create">
            <SpecialTaskWithProvider />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/analysis"
        element={
          <ProtectedRoute requiredPermission="tasks:read">
            <SpecialTasksAnalysisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/current-plan"
        element={
          <ProtectedRoute requiredPermission="plans:read">
            <CurrentPlan />
          </ProtectedRoute>
        }
      />

      <Route
        path="/damage/machines"
        element={
          <ProtectedRoute requiredPermission="machines:manage">
            <MachineList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/machines/new"
        element={
          <ProtectedRoute requiredPermission="machines:manage">
            <MachineEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/machines/:id/edit"
        element={
          <ProtectedRoute requiredPermission="machines:manage">
            <MachineEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/machines/:id"
        element={
          <ProtectedRoute requiredPermission="machines:manage">
            <MachineView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/drivers"
        element={
          <ProtectedRoute requiredPermission="drivers:manage">
            <DriversList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/drivers/new"
        element={
          <ProtectedRoute requiredPermission="drivers:manage">
            <DriverEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/drivers/:id/edit"
        element={
          <ProtectedRoute requiredPermission="drivers:manage">
            <DriverEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/grooming-data"
        element={
          <ProtectedRoute requiredPermission="resources:read">
            <GroomingDataList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/grooming-data/new"
        element={
          <ProtectedRoute requiredPermission="resources:manage">
            <GroomingDataForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/grooming-data/:id/edit"
        element={
          <ProtectedRoute requiredPermission="resources:manage">
            <GroomingDataForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damage/grooming-data/:id/view"
        element={
          <ProtectedRoute requiredPermission="resources:read">
            <GroomingDataView />
          </ProtectedRoute>
        }
      />

      {/* Route plate : MainDashboard rend ses propres <Routes> sans <Outlet/>,
          une route imbriquee sous /admin/* serait morte a l execution. */}
      <Route
        path="/damage/pistes"
        element={
          <ProtectedRoute requiredPermission="slopes:manage" requiredModule="damage">
            <SlopeEditorPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/damage/statistiques"
        element={
          <ProtectedRoute requiredPermission="resources:read" requiredModule="damage">
            <GroomingAnalyticsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="marker-categories"
        element={
          <ProtectedRoute requiredPermission="admin:manage_roles">
            <MarkerCategoriesAdmin />
          </ProtectedRoute>
        }
      />

      <Route
        path="/chart/*"
        element={
          <ProtectedRoute requiredPermission="data:view_meteo">
            <ChartDashboard />
          </ProtectedRoute>
        }
      >
        <Route path="" element={<ChioneTab />} />
        <Route path="bar" element={<BarChart />} />
        <Route path="line" element={<LineChart />} />
        <Route path="pie" element={<PieChart />} />
        <Route path="temperature" element={<TemperatureChart />} />
        <Route path="epneige" element={<EpNeige />} />
      </Route>

      {/* What's New (authenticated; admin literals before :slug) */}
      <Route
        path="/whats-new/admin"
        element={
          <ProtectedRoute requiredPermission="whatsnew:manage">
            <WhatsNewAdminList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new/admin/new"
        element={
          <ProtectedRoute requiredPermission="whatsnew:manage">
            <WhatsNewEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new/admin/:id/edit"
        element={
          <ProtectedRoute requiredPermission="whatsnew:manage">
            <WhatsNewEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new/:slug"
        element={
          <ProtectedRoute>
            <WhatsNewPostView />
          </ProtectedRoute>
        }
      />

      {/* Vue 3D dediee : HORS du layout Main (pas de sidebar), ouverte dans un nouvel
          onglet depuis le menu d une couche. Authentification seule — la garde reelle
          est cote API, ou la liste est deja bornee au site actif et filtree par
          visibilite. */}
      <Route
        path="/viewer3d/:layerId"
        element={
          <ProtectedRoute>
            <Model3dViewer />
          </ProtectedRoute>
        }
      />

      {/* 404 Not Found (must be the last route) */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
